import { useCallback, useEffect, useState } from "react";
import { ApiError, get } from "./api";

interface Loaded<T> {
  key: string;
  data?: T;
  error?: ApiError;
}

/**
 * Reads an API path and reloads on demand. While a new path loads, the
 * previous data stays on screen, so lists do not flash empty.
 */
export function useResource<T>(path: string) {
  const [version, setVersion] = useState(0);
  const key = `${version}:${path}`;
  const [state, setState] = useState<Loaded<T>>({ key: "" });

  useEffect(() => {
    let live = true;
    get<T>(path).then(
      (data) => live && setState({ key, data }),
      (error: ApiError) => live && setState((s) => ({ key, data: s.data, error })),
    );
    return () => {
      live = false;
    };
  }, [path, key]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);
  return { data: state.data, error: state.error, loading: state.key !== key, reload };
}

/** Runs one change at a time and keeps its error for the form to show. */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const run = useCallback(async <R>(action: () => Promise<R>): Promise<R | undefined> => {
    setBusy(true);
    setError(null);
    try {
      return await action();
    } catch (e) {
      setError(e instanceof ApiError ? e : new ApiError(0, "unknown", String(e)));
      return undefined;
    } finally {
      setBusy(false);
    }
  }, []);
  return { busy, error, run, clear: () => setError(null) };
}
